/* ═══════════════════════════════════════════════════════════════════════
   Fotografía de la landing
   ═══════════════════════════════════════════════════════════════════════

   Todas las fotos son de Unsplash y TODAS fueron verificadas una por una
   (HTTP 200 + revisadas a ojo antes de elegirlas). No inventes IDs: los que
   «parecen» de Unsplash devuelven 404 y se publican como imagen rota.

   Criterio de selección — se buscó el objeto físico, no la categoría:
   «carpintero clavando armazón a contraluz», no «construcción». Se
   descartaron a propósito las fotos con chaleco verde limón o casco rojo:
   compiten con el amarillo de marca dentro del mismo encuadre.

   Cuando el equipo mande fotos propias (su gente, sus obras), estas se
   reemplazan aquí y ya — ningún componente conoce una URL directamente.
   ═══════════════════════════════════════════════════════════════════════ */

const UNSPLASH = 'https://images.unsplash.com/photo-';

/** Ancho servido en cada escalón del srcset. */
const WIDTHS = [960, 1440, 1920, 2560] as const;

export interface Photo {
    src: string;
    srcSet: string;
    /** CLAVE del diccionario, no el texto: se traduce en quien pinta la
     *  imagen. Este archivo se evalúa al importarse y ahí no hay hook. */
    alt: string;
    /**
     * Punto de interés. Unsplash recorta al centro por defecto y en varias de
     * estas la persona no está en el centro; sin esto se le corta la cabeza en
     * formatos anchos.
     */
    position: string;
}

function photo(id: string, alt: string, position = '50% 50%'): Photo {
    const url = (w: number) => `${UNSPLASH}${id}?auto=format&fit=crop&w=${w}&q=72`;
    return {
        src: url(1920),
        srcSet: WIDTHS.map((w) => `${url(w)} ${w}w`).join(', '),
        alt,
        position,
    };
}

/** Carpintero clavando el armazón de una casa nueva, contra cielo abierto. */
export const OFICIO = photo(
    '1646324554833-f0b6a479fa5d',
    'lp.alt.oficio',
    '50% 45%',
);

/** Trabajador de espaldas con casco amarillo; el amarillo de la foto ES el de marca. */
export const CASCO = photo(
    '1614213951697-a45781262acf',
    'lp.alt.casco',
    '50% 40%',
);

/** Cuadrilla completa parada frente a una losa recién colada, vista desde arriba. */
export const CUADRILLA = photo(
    '1541888946425-d81bb19240f5',
    'lp.alt.cuadrilla',
    '50% 60%',
);

/* ═══════════════════════════════════════════════════════════════════════
   Vídeo del hero
   ═══════════════════════════════════════════════════════════════════════

   El original del equipo (Supabase Storage) pesaba 6.876 KB: 1920×1080 a
   6.889 kb/s MÁS una pista de audio AAC de 139 kb/s que no suena nunca,
   porque el elemento va `muted` por definición. Para un fondo tapado por un
   degradado eso es una barbaridad, y en un teléfono con datos era el 78 % de
   todo lo que descargaba la página.

   Reencodado (mismo metraje, mismo encuadre, sin audio) y servido desde el
   propio dominio, que además ahorra la conexión a un tercero:

     escritorio   1920  VP9/WebM  1.185 KB   ·  H.264/MP4  1.288 KB
     teléfono      960  VP9/WebM    373 KB   ·  H.264/MP4    322 KB

   Se eligió VP9 midiendo, no por fe: a igual calidad objetiva (SSIM 0,958)
   VP9 daba 1.409 KB contra 1.492 KB de H.264. El MP4 se mantiene porque
   Safari todavía no reproduce VP9 en <video>.
   ═══════════════════════════════════════════════════════════════════════ */

export interface FuenteVideo {
    webm: string;
    mp4: string;
}

/** 960 px de ancho: un teléfono de 390 px a 3x pide 1170, y va bajo degradado. */
export const HERO_VIDEO_MOVIL: FuenteVideo = {
    webm: '/video/hero-960.webm',
    mp4: '/video/hero-960.mp4',
};

export const HERO_VIDEO: FuenteVideo = {
    webm: '/video/hero-1920.webm',
    mp4: '/video/hero-1920.mp4',
};

/** Primer fotograma. Se pinta al instante mientras el vídeo llega. */
export const HERO_POSTER = '/video/hero-poster.webp';
